/**
 * Runtime validation at the client boundary.
 *
 * Generated types vanish at compile time. If the server returns something the
 * spec does not describe — a hand-rolled `json!` in a not-yet-converted
 * handler, a field that is null where the spec says string, a rename that
 * shipped before the client regenerated — TypeScript believes the lie and the
 * UI renders blanks. That is how the `buyer_price` column sat empty for four
 * months looking like missing data rather than a bug.
 *
 * Two rules, both deliberate:
 *
 * 1. **`safeParse`, never `parse`.** A mismatch must not throw. A backend
 *    adding a nullable field should never turn a blank column into a blank
 *    page, and a validator that takes the app down is worse than the drift it
 *    was added to catch.
 *
 * 2. **Report, then render anyway.** The data is returned unchanged on
 *    failure. Validation is a smoke alarm, not a lock on the door — its job is
 *    to make drift an attributable event on the day it happens rather than a
 *    mystery four months later.
 */
import type { ZodType } from 'zod';
import * as schemas from './zod';

/** Where drift gets reported. Swap for an error service when there is one. */
function reportDrift(schemaName: string, issues: unknown): void {
  console.error(
    `[api-drift] response did not match the published schema for ${schemaName}. ` +
      'The server and this client disagree about its shape; rendering it anyway.',
    issues,
  );
}

const registry = schemas as unknown as Record<string, ZodType | undefined>;

/**
 * Check `data` against the named schema from the generated module.
 *
 * Always returns the data. An unknown schema name is not an error either —
 * endpoints get converted gradually, and a screen should not break because its
 * response is not spec'd yet.
 */
export function validated<T>(schemaName: string, data: T): T {
  const schema = registry[schemaName];
  if (!schema) return data;

  const result = schema.safeParse(data);
  if (!result.success) reportDrift(schemaName, result.error.issues);
  return data;
}
